import * as React from "react";

import { api } from "@repo/backend";
import { Button } from "@repo/ui/components/button";
import { useConfirmationDialog } from "@repo/ui/components/confirmation-dialog-provider";
import { Skeleton } from "@repo/ui/components/skeleton";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { ConvexError } from "convex/values";
import { format, parseISO } from "date-fns";
import { AlertTriangleIcon, CheckIcon, Loader2Icon, XIcon } from "lucide-react";
import { toast } from "sonner";

import { timezoneLabel } from "@/components/scheduling/scheduling-layout";

export const Route = createFileRoute("/scheduling/confirmed/$token")({
  head: () => ({
    meta: [
      { title: "Booking Confirmed | WorkGrid" },
      {
        name: "description",
        content:
          "Your staff photo session is confirmed. A confirmation email with your booking details has been sent — check your inbox for your Voxis links.",
      },
    ],
  }),
  component: BookingConfirmation,
});

const fullDate = (date: string) => format(parseISO(`${date}T12:00:00`), "EEEE, MMMM d, yyyy");

function BookingConfirmation() {
  const { token } = Route.useParams();
  const navigate = useNavigate({ from: Route.fullPath });
  const confirm = useConfirmationDialog();
  const confirmation = useQuery(api.scheduling.getBookingConfirmation, { token });
  const cancelBooking = useMutation(api.scheduling.cancelBooking);
  const [isCancelling, setIsCancelling] = React.useState(false);

  if (confirmation === undefined) return <ConfirmationSkeleton />;

  if (!confirmation) {
    return (
      <section className="flex items-center justify-center bg-[#191919] p-8 text-white">
        <p className="text-sm text-white/55">Booking confirmation not found.</p>
      </section>
    );
  }

  const cancel = async () => {
    const accepted = await confirm({
      title: "Cancel this booking?",
      description: "This session will be cancelled and the time will become available again.",
      confirmText: "Cancel booking",
      confirmButtonVariant: "destructive",
      media: <AlertTriangleIcon />,
    });
    if (!accepted) return;

    setIsCancelling(true);
    try {
      await cancelBooking({ token });
      toast.success("Booking cancelled.");
    } catch (error) {
      toast.error(error instanceof ConvexError ? String(error.data) : "Could not cancel booking.");
    } finally {
      setIsCancelling(false);
    }
  };

  const { config, slot, name, email, cancelledAt } = confirmation;
  return (
    <section className="flex items-center justify-center bg-[#191919] p-6 text-white">
      <div className="flex w-full max-w-md flex-col items-center px-6 py-10 text-center">
        <div
          className={
            cancelledAt
              ? "flex size-12 items-center justify-center rounded-full bg-white/10 text-white/55"
              : "flex size-12 items-center justify-center rounded-full bg-green-400/15 text-green-400"
          }
        >
          {cancelledAt ? <XIcon className="size-6" /> : <CheckIcon className="size-6" />}
        </div>
        <h1 className="mt-4 text-xl font-semibold italic">
          {cancelledAt ? "Booking cancelled" : "Booking confirmed"}
        </h1>
        <p className="mt-2 text-sm text-white/55">
          {cancelledAt
            ? "This time is available to book again."
            : `Thanks, ${name}. Your photo session is booked.`}
        </p>
        <div className="mt-6 w-full rounded-lg border border-white/10 bg-white/[0.03] p-4 text-left text-sm">
          <p className="font-medium">{config.name}</p>
          <p className="mt-1 text-white/75">{config.sessionLabel}</p>
          <p className="mt-2 text-white/55">{fullDate(slot.localDate)}</p>
          <p className="text-white/55">
            {slot.localStartTime}–{slot.localEndTime} · {timezoneLabel(config.timezone)}
          </p>
          <p className="mt-3 border-t border-white/10 pt-3 text-white/55">
            Confirmation for {email}
          </p>
        </div>
        <div className="mt-6 flex w-full flex-col gap-2">
          {!cancelledAt ? (
            <Button variant="destructive" disabled={isCancelling} onClick={() => void cancel()}>
              {isCancelling ? <Loader2Icon className="animate-spin" /> : null}
              Cancel booking
            </Button>
          ) : null}
          <Button variant="outline" onClick={() => navigate({ to: "/scheduling" })}>
            Book again
          </Button>
        </div>
      </div>
    </section>
  );
}

function ConfirmationSkeleton() {
  return (
    <section className="flex items-center justify-center bg-[#191919] p-6">
      <div className="flex w-full max-w-md flex-col items-center px-6 py-10">
        <Skeleton className="size-12 rounded-full bg-white/10" />
        <Skeleton className="mt-4 h-6 w-44 bg-white/10" />
        <Skeleton className="mt-3 h-4 w-64 bg-white/10" />
        <div className="mt-6 w-full space-y-3 rounded-lg border border-white/10 p-4">
          <Skeleton className="h-4 w-3/4 bg-white/10" />
          <Skeleton className="h-4 w-1/2 bg-white/10" />
          <Skeleton className="h-4 w-2/3 bg-white/10" />
          <Skeleton className="h-4 w-full bg-white/10" />
        </div>
        <Skeleton className="mt-6 h-9 w-full bg-white/10" />
        <Skeleton className="mt-2 h-9 w-full bg-white/10" />
      </div>
    </section>
  );
}
